import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { HealthStatus, VersionInfo } from '@/types/api';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

/**
 * Readiness is fetched directly (not via apiClient) because the endpoint returns
 * HTTP 503 when a dependency is down — which is the correct orchestrator signal,
 * but here we want to render the per-dependency detail regardless of status.
 */
async function fetchReadiness(): Promise<HealthStatus> {
  const res = await fetch(`${API_BASE}/health/ready`, { headers: { Accept: 'application/json' } });
  const body = (await res.json()) as { data: HealthStatus };
  return body.data;
}

export function SystemStatusPage() {
  const version = useQuery({
    queryKey: ['version'],
    queryFn: () => apiClient.get<VersionInfo>('/version'),
  });

  const readiness = useQuery({
    queryKey: ['readiness'],
    queryFn: fetchReadiness,
    refetchInterval: 15_000,
  });

  // Said in words while the answer is on its way or did not come, never as a bare dash.
  const unknown = version.isLoading ? 'Loading' : 'Not available';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">System Status</h1>
        <p className="text-sm text-muted-foreground">Live health of the platform's core services.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Dependencies</CardTitle>
          <CardDescription>Checked every 15 seconds against the readiness probe.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {readiness.isLoading && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Checking…
            </p>
          )}
          {readiness.isError && (
            <p className="flex items-center gap-2 text-sm text-danger">
              <XCircle className="size-4" /> API unreachable.
            </p>
          )}
          {readiness.data &&
            Object.entries(readiness.data.checks ?? {}).map(([name, check]) => {
              const up = check.status === 'up';
              return (
                <div
                  key={name}
                  className="flex items-center justify-between rounded-md border border-border bg-muted/20 px-4 py-3"
                >
                  <span className="font-medium capitalize">{name}</span>
                  <span className={cn('flex items-center gap-2 text-sm', up ? 'text-success' : 'text-danger')}>
                    {up ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
                    {up ? 'Operational' : (check.error ?? 'Down')}
                  </span>
                </div>
              );
            })}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Platform</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-4 text-sm">
          {/* "Build", "API contract" and a raw "prod" are how engineers talk about a deployment.
              This page is read by customers checking whether the platform is healthy, so the
              labels say what the values mean to them. */}
          <Detail label="Product" value={version.data?.application ?? unknown} />
          <Detail label="Release" value={version.data?.version ?? unknown} mono />
          <Detail label="API version" value={version.data?.api_version ?? unknown} mono />
          <Detail label="Deployment" value={environmentLabel(version.data?.environment) ?? unknown} />
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Turns the raw environment name into something a customer can read.
 *
 * The API returns whatever `APP_ENV` holds — `prod`, `dev`, `staging`. Those are deployment
 * shorthand, and "prod" on a status page tells a customer nothing except that they are reading
 * somebody's configuration file.
 */
function environmentLabel(environment: string | undefined): string | undefined {
  switch (environment) {
    case 'prod':
    case 'production':
      return 'Production';
    case 'staging':
      return 'Staging';
    case 'dev':
    case 'development':
      return 'Development';
    default:
      return environment;
  }
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn('mt-0.5', mono && 'font-mono text-[13px]')}>{value}</p>
    </div>
  );
}
